import cn from "classnames";
import { memo, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { analytics } from "@rshbintech.dboul/analytics";
import {
  ChevronUp as ChevronUpIcon,
  ChevronDown as ChevronDownIcon,
} from "@rshbintech.dboul/icons";
import { Text, useMediaQuery } from "@rshbintech.dboul/core-ui";
import { createMetricDataObject } from "../../../common/utils/create-metric-data-object";
import { MenuItem } from "../menu-item";
import { ExpandableMenuItemProps } from "./types";
import styles from "./styles.scss";

export const ExpandableMenuItem = memo(
  ({
    name,
    disabled,
    metricName,
    metricData,
    dataTestId,
    icon: Icon,
    isMenuOpen,
    onMenuItemClick,
    additionalItems,
  }: ExpandableMenuItemProps) => {
    const [isOpenAdditionalLinks, setIsOpenAdditionalLinks] = useState(false);
    const isDesktop = useMediaQuery("desktop");
    const xl = useMediaQuery("xl");
    const xxl = useMediaQuery("xxl");

    const location = useLocation();

    useEffect(() => {
      if (
        additionalItems?.some(
          ({ path }) => !!location.pathname.startsWith(path)
        )
      ) {
        setIsOpenAdditionalLinks(true);
      }
    }, [additionalItems, location.pathname]);

    if (disabled) {
      return null;
    }

    const handleLinksOpen = () => {
      setIsOpenAdditionalLinks(!isOpenAdditionalLinks);
    };

    const handleMenuItemClick = () => {
      analytics.sendEvent(metricName, createMetricDataObject(metricData));

      if (isMenuOpen) {
        onMenuItemClick?.();
      }
    };

    return (
      <div>
        <span
          onClick={() => {
            handleLinksOpen();
          }}
          data-test-id={dataTestId}
          className={cn(
            styles["menu-item"],
            isOpenAdditionalLinks && styles["menu-additional-item"],
            isDesktop && !isOpenAdditionalLinks && styles["menu-main-item"],
            {
              [styles.active]: isOpenAdditionalLinks,
            }
          )}
        >
          {Icon && (
            <Icon
              className={styles.icon}
              size={xl || xxl ? "medium" : "small"}
            />
          )}

          <Text
            weight={isOpenAdditionalLinks ? "medium" : "regular"}
            className={styles.text}
          >
            {(isDesktop || isMenuOpen) && name}
          </Text>

          {isOpenAdditionalLinks ? (
            <ChevronUpIcon size="medium" />
          ) : (
            <ChevronDownIcon size="medium" />
          )}
        </span>
        {isOpenAdditionalLinks && (
          <nav
            data-test-id="menu-block-middle"
            className={styles["menu-block-saas"]}
          >
            {additionalItems &&
              additionalItems.map(({ icon: Icon, ...menuItem }) => (
                <MenuItem
                  key={menuItem.name}
                  name={menuItem.name}
                  path={menuItem.path}
                  icon={({ className, ...props }) =>
                    Icon && (
                      <Icon
                        {...props}
                        className={cn(className, {
                          [styles.transparent]: isDesktop || isMenuOpen,
                        })}
                      />
                    )
                  }
                  disabled={menuItem.disabled}
                  dataTestId={menuItem.dataTestId}
                  metricName={menuItem.metricName}
                  metricData={menuItem.metricData}
                  featureName={menuItem.featureName}
                  onMenuItemClick={handleMenuItemClick}
                  isMenuOpen={isMenuOpen}
                />
              ))}
          </nav>
        )}
      </div>
    );
  }
);
